import React from 'react';

import { OTPSlotProps } from '@/shared/components/ui/form/components/form-input/variant/otp-form-input/components/otp-slot/otp-slot.types';

export const OTPSlot: React.FC<OTPSlotProps> = ({
  value,
  index,
  length,
  currentOtpValue,
  onOtpChange,
  inputRefs,
  ref,
  ...other
}) => {
  const focusInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput?.focus();
  };

  const selectInput = (targetIndex: number) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput?.select();
  };

  const buildOtpString = (newCharAtIndex: string) => {
    const otpDigits = [];
    for (let i = 0; i < length; i++) {
      if (i === index) {
        otpDigits.push(newCharAtIndex);
      } else {
        otpDigits.push(currentOtpValue?.[i] ?? '');
      }
    }
    return otpDigits.join('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      onOtpChange(buildOtpString(event.key));

      if (index < length - 1) {
        setTimeout(() => {
          focusInput(index + 1);
          selectInput(index + 1);
        }, 0);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case ' ':
        event.preventDefault();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (index > 0) {
          focusInput(index - 1);
          selectInput(index - 1);
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (index < length - 1) {
          focusInput(index + 1);
          selectInput(index + 1);
        }
        break;
      case 'Delete':
        event.preventDefault();
        onOtpChange(buildOtpString(''));
        break;
      case 'Backspace':
        event.preventDefault();
        onOtpChange(buildOtpString(''));
        if (index > 0) {
          focusInput(index - 1);
          selectInput(index - 1);
        }
        break;
      default:
        break;
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const currentValue = rawValue.replace(/[^0-9]/g, '');

    if (currentValue.length === 0) {
      return;
    }

    onOtpChange(buildOtpString(currentValue[currentValue.length - 1]));

    if (index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
  };

  const handleClick = () => {
    selectInput(index);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const clipboardData = event.clipboardData;

    if (clipboardData.types.includes('text/plain')) {
      let pastedText = clipboardData.getData('text/plain');
      pastedText = pastedText.replace(/[^0-9]/g, '');
      pastedText = pastedText.substring(0, length).trim();

      if (pastedText.length === 0) {
        return;
      }

      // Build from current value, overlay pasted content starting at current index
      const otpDigits = (currentOtpValue ?? '').padEnd(length, '').split('');
      for (let i = 0; i < pastedText.length && index + i < length; i++) {
        otpDigits[index + i] = pastedText[i];
      }
      onOtpChange(otpDigits.join(''));

      const newFocusIndex = Math.min(index + pastedText.length, length - 1);
      focusInput(newFocusIndex);
    }
  };

  return (
    <input
      {...other}
      ref={(element) => {
        if (typeof ref === 'function') {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
        if (element && inputRefs?.current) {
          inputRefs.current[index] = element as HTMLInputElement;
        }
      }}
      value={value || ''}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      onClick={handleClick}
      onFocus={handleFocus}
      onPaste={handlePaste}
      type="tel"
      pattern="[0-9]*"
      inputMode="numeric"
      maxLength={1}
    />
  );
};
